import React, { useState, useEffect } from 'react';
import { authFetch } from '../utils/api';
import './Charts.css';

const Charts = () => {
  const [users, setUsers] = useState([]);
  const [quotesData, setQuotesData] = useState([]);
  const [rawQuotes, setRawQuotes] = useState([]); // Store raw quotes for debugging
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState('month'); // week, month, quarter, year
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'byStatus'
  const [selectedUser, setSelectedUser] = useState('all'); // 'all' or specific user id
  const [showDebug, setShowDebug] = useState(false); // Toggle debug panels

  // Status colors mapping
  const statusColors = {
    'Quote':  'lime',
    'pending': '#FFA500',
    'approved': '#4CAF50',
    'rejected': '#F44336',
    'in_progress': '#2196F3',
    'completed': '#9C27B0',
    'cancelled': '#757575',
    'default': '#9E9E9E'
  };

  useEffect(() => {
    fetchData();
  }, [timePeriod]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch users
      const usersData = await authFetch('/users/');

      // Fetch quotes
      const quotes = await authFetch('/quotes/');

      // Store raw quotes for debugging
      setRawQuotes(quotes);

      // Filter quotes by time period
      const filteredQuotes = filterByTimePeriod(quotes, timePeriod);

      // Group quotes by user
      const userQuotesMap = {};

      filteredQuotes.forEach(quote => {
        const userId = quote.user_id || quote.userId || quote.fkUserId;

        // Skip quotes without a user ID
        if (!userId) {
          console.warn('Quote without user_id:', quote);
          return;
        }

        if (!userQuotesMap[userId]) {
          userQuotesMap[userId] = {
            quotes: [],
            quoteItems: 0,
            statusCounts: {}
          };
        }
        userQuotesMap[userId].quotes.push(quote);
        userQuotesMap[userId].quoteItems += quote.items_count || 0;

        // Count by status
        const status = quote.status || 'default';
        userQuotesMap[userId].statusCounts[status] =
          (userQuotesMap[userId].statusCounts[status] || 0) + 1;
      });

      setUsers(usersData);
      setQuotesData(userQuotesMap);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const filterByTimePeriod = (quotes, period) => {
    const now = new Date();
    const cutoffDate = new Date();

    switch (period) {
      case 'week':
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        cutoffDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        cutoffDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        return quotes;
    }

    return quotes.filter(quote => {
      const quoteDate = new Date(quote.created_at || quote.createdAt);
      return quoteDate >= cutoffDate;
    });
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? (user.username || `User ${userId}`) : `User ${userId}`;
  };

  const getFilteredQuotesData = () => {
    if (selectedUser === 'all') {
      return quotesData;
    }
    const filtered = {};
    if (quotesData[selectedUser]) {
      filtered[selectedUser] = quotesData[selectedUser];
    }
    return filtered;
  };

  const renderAllQuotesChart = () => {
    const filteredData = getFilteredQuotesData();
    const maxQuotes = Math.max(...Object.values(filteredData).map(d => d.quotes.length), 1);
    const maxItems = Math.max(...Object.values(filteredData).map(d => d.quoteItems), 1);

    return (
      <div className="chart-container">
        <h3>User Quotes and Quote Items</h3>
        <div className="chart-grid">
          {Object.entries(filteredData).map(([userId, data]) => {
            const quotesHeight = (data.quotes.length / maxQuotes) * 200;
            const itemsHeight = (data.quoteItems / maxItems) * 200;

            return (
              <div key={userId} className="user-chart-column">
                <div className="user-name">{getUserName(parseInt(userId))}</div>
                <div className="bars-container">
                  <div className="bar-wrapper">
                    <div
                      className="chart-bar quotes-bar"
                      style={{ height: `${quotesHeight}px` }}
                      title={`${data.quotes.length} quotes`}
                    >
                      <span className="bar-label">{data.quotes.length}</span>
                    </div>
                    <div className="bar-title">Quotes</div>
                  </div>
                  <div className="bar-wrapper">
                    <div
                      className="chart-bar items-bar"
                      style={{ height: `${itemsHeight}px` }}
                      title={`${data.quoteItems} items`}
                    >
                      <span className="bar-label">{data.quoteItems}</span>
                    </div>
                    <div className="bar-title">Items</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderByStatusChart = () => {
    const filteredData = getFilteredQuotesData();

    return (
      <div className="chart-container">
        <h3>Quotes by Status</h3>
        <div className="chart-grid">
          {Object.entries(filteredData).map(([userId, data]) => {
            const totalQuotes = data.quotes.length;

            return (
              <div key={userId} className="user-chart-column">
                <div className="user-name">{getUserName(parseInt(userId))}</div>
                <div className="status-bars-container">
                  {Object.entries(data.statusCounts).map(([status, count]) => {
                    const height = (count / totalQuotes) * 200;
                    const color = statusColors[status] || statusColors.default;

                    return (
                      <div key={status} className="status-bar-wrapper">
                        <div
                          className="chart-bar status-bar"
                          style={{
                            height: `${height}px`,
                            backgroundColor: color
                          }}
                          title={`${status}: ${count} quotes`}
                        >
                          <span className="bar-label">{count}</span>
                        </div>
                        <div className="bar-title">{status}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="legend">
          <h4>Status Legend:</h4>
          <div className="legend-items">
            {Object.entries(statusColors).map(([status, color]) => (
              <div key={status} className="legend-item">
                <div
                  className="legend-color"
                  style={{ backgroundColor: color }}
                />
                <span>{status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Loading statistics...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="charts-container">
      <div className="controls">
        <div className="control-group">
          <label>Time Period:</label>
          <select value={timePeriod} onChange={(e) => setTimePeriod(e.target.value)}>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
        </div>

        <div className="control-group">
          <label>View Mode:</label>
          <select value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
            <option value="all">All Quotes & Items</option>
            <option value="byStatus">By Status</option>
          </select>
        </div>

        <div className="control-group">
          <label>User:</label>
          <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
            <option value="all">All Users</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.username || `User ${user.id}`}
              </option>
            ))}
          </select>
        </div>

        <button onClick={fetchData} className="refresh-button">
          Refresh Data
        </button>

        {false && (
          <button
            onClick={() => setShowDebug(!showDebug)}
            className="refresh-button"
            style={{ marginLeft: '10px' }}
          >
            {showDebug ? 'Hide Debug' : 'Show Debug'}
          </button>
        )}
      </div>


      {/* Debug: Display raw data */}
      {showDebug && (
      <>
        <div className="chart-container">
          <h3>Debug: Users Endpoint Data</h3>
          <pre style={{
            backgroundColor: '#f4f4f4',
            padding: '10px',
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '300px',
            fontSize: '11px'
          }}>
            {JSON.stringify(users, null, 2)}
          </pre>
        </div>

        <div className="chart-container">
          <h3>Debug: Raw Quotes from API (First 3)</h3>
          <pre style={{
            backgroundColor: '#f4f4f4',
            padding: '10px',
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '300px',
            fontSize: '11px'
          }}>
            {JSON.stringify(rawQuotes.slice(0, 3), null, 2)}
          </pre>
        </div>

        <div className="chart-container">
          <h3>Debug: Grouped Quotes Data</h3>
          <pre style={{
            backgroundColor: '#f4f4f4',
            padding: '10px',
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '300px',
            fontSize: '11px'
          }}>
            {JSON.stringify(quotesData, null, 2)}
          </pre>
        </div>
      </>
      )}

      {Object.keys(quotesData).length === 0 ? (
        <div className="no-data">No data available for the selected time period</div>
      ) : (
        <>
          {viewMode === 'all' && renderAllQuotesChart()}
          {viewMode === 'byStatus' && renderByStatusChart()}
        </>
      )}
    </div>
  );
};

export default Charts;
